import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Stack,
} from "@mui/material";

import VerifiedIcon from "@mui/icons-material/Verified";
import EventIcon from "@mui/icons-material/Event";
import PublicIcon from "@mui/icons-material/Public";

function getStatusStyle(status) {
  switch (status?.toLowerCase()) {
    case "least concern":
      return {
        bg: "#E8F5E9",
        color: "#2E7D32",
      };

    case "near threatened":
      return {
        bg: "#FFF8E1",
        color: "#F9A825",
      };

    case "vulnerable":
      return {
        bg: "#FFF3E0",
        color: "#EF6C00",
      };

    case "endangered":
      return {
        bg: "#FFEBEE",
        color: "#C62828",
      };

    case "critically endangered":
      return {
        bg: "#FDECEA",
        color: "#B71C1C",
      };

    default:
      return {
        bg: "#EEF5FA",
        color: "#0B3C5D",
      };
  }
}

function ConservationStatus({ plant }) {
  if (!plant?.conservation_status) return null;

  const { iucn_status, assessment_year } =
    plant.conservation_status;

  const statusStyle = getStatusStyle(iucn_status);

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 5,
        p: {
          xs: 3,
          md: 4,
        },
        borderRadius: 5,

        background:
          "linear-gradient(135deg,#FFFFFF,#F8FBFC)",

        border: "1px solid #E4EDF2",

        boxShadow: "0 10px 30px rgba(11,60,93,.08)",
      }}
    >
      {/* Header */}

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        mb={3}
      >
        <Box
          sx={{
            width: 55,
            height: 55,
            borderRadius: "50%",
            bgcolor: "#EEF5FA",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <VerifiedIcon
            sx={{
              color: "#0B3C5D",
            }}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: {
                xs: "1.5rem",
                md: "2rem",
              },
              fontWeight: 700,
              color: "#0B3C5D",
            }}
          >
            Conservation Status
          </Typography>

          <Typography
            color="text.secondary"
            fontSize={15}
          >
            IUCN Red List assessment and conservation information
          </Typography>
        </Box>
      </Stack>

      {/* Accent */}

      <Box
        sx={{
          width: 90,
          height: 4,
          bgcolor: "#43A047",
          borderRadius: 10,
          mb: 4,
        }}
      />

      {/* Cards */}

      <Grid container spacing={3}>
        {/* IUCN */}

        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              height: "100%",

              background:
                "linear-gradient(135deg,#FFFFFF,#F9FCFD)",

              border: "1px solid #E5EDF3",

              boxShadow:
                "0 6px 18px rgba(11,60,93,.05)",

              transition: ".35s",

              "&:hover": {
                transform: "translateY(-6px)",
                boxShadow:
                  "0 15px 35px rgba(11,60,93,.12)",
              },
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              mb={2}
            >
              <VerifiedIcon
                sx={{
                  color: "#0B3C5D",
                }}
              />

              <Typography
                fontWeight={700}
                color="#0B3C5D"
              >
                IUCN Conservation Status
              </Typography>
            </Stack>

            <Chip
              label={iucn_status}
              sx={{
                bgcolor: statusStyle.bg,
                color: statusStyle.color,
                fontWeight: 700,
                fontSize: "0.95rem",
              }}
            />
          </Paper>
        </Grid>

        {/* Year */}

        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              height: "100%",

              background:
                "linear-gradient(135deg,#FFFFFF,#F9FCFD)",

              border: "1px solid #E5EDF3",

              boxShadow:
                "0 6px 18px rgba(11,60,93,.05)",

              transition: ".35s",

              "&:hover": {
                transform: "translateY(-6px)",
                boxShadow:
                  "0 15px 35px rgba(11,60,93,.12)",
              },
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              mb={2}
            >
              <EventIcon
                sx={{
                  color: "#0B3C5D",
                }}
              />

              <Typography
                fontWeight={700}
                color="#0B3C5D"
              >
                Assessment Year
              </Typography>
            </Stack>

            <Typography
              sx={{
                fontSize: "2.5rem",
                fontWeight: 800,
                color: "#0B3C5D",
              }}
            >
              {assessment_year}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Footer Note */}

      <Box
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 4,

          bgcolor: "#F5FAF7",

          borderLeft: "5px solid #43A047",

          display: "flex",
          gap: 2,
          alignItems: "flex-start",
        }}
      >
        <PublicIcon
          sx={{
            color: "#2E7D32",
            mt: 0.3,
          }}
        />

        <Typography
          sx={{
            color: "#555",
            lineHeight: 1.9,
          }}
        >
          <strong style={{ color: "#0B3C5D" }}>
            Conservation Note:
          </strong>{" "}
          The conservation status shown above follows the
          International Union for Conservation of Nature (IUCN)
          Red List assessment. It indicates the extinction risk of
          the species and supports biodiversity conservation,
          habitat management, restoration planning, and sustainable
          utilization. Regular reassessment is essential for
          effective conservation planning.
        </Typography>
      </Box>
    </Paper>
  );
}

export default ConservationStatus;